"use client";

import { useCallback, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { WebImport, WebImportButton } from "@/components/ui/web-import";
import { showToast } from "@/components/ui/sonner";
import { useWebImport } from "@/lib/api/generated";
import { CoverUpload } from "@/features/projects/components/cover-upload";
import { AccessLevelField } from "@/features/projects/components/access-level-field";
import { DEFAULT_COVER } from "@/lib/image/covers";
import type { OnboardingFormData } from "../../hooks/use-onboarding-wizard";

export function StepProject() {
  const { control, setValue, getValues } = useFormContext<OnboardingFormData>();
  const coverPreviewUrl = useWatch({
    control,
    name: "project.cover_preview_url",
  });
  const objectUrlRef = useRef<string | null>(null);

  const setCoverFromFile = useCallback(
    (file: File) => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(file);
      objectUrlRef.current = url;
      setValue("project.cover_file", file, { shouldDirty: true });
      setValue("project.cover_preview_url", url, { shouldDirty: true });
    },
    [setValue],
  );

  useEffect(() => {
    if (getValues("project.cover_preview_url")) return;
    setValue("project.cover_preview_url", DEFAULT_COVER, { shouldDirty: false });
  }, [getValues, setValue]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  const webImportMutation = useWebImport({
    mutation: {
      onSuccess: (result) => {
        setValue("project.title", result.project_title, {
          shouldValidate: true,
          shouldDirty: true,
        });
        showToast.success("Imported successfully");
      },
      onError: (err: unknown) => {
        const message =
          err instanceof Error ? err.message : "Failed to import.";
        showToast.error("Import failed", { description: message });
      },
    },
  });

  const handleWebImport = useCallback(
    async (url: string) => {
      await webImportMutation.mutateAsync({ data: { url } });
    },
    [webImportMutation],
  );

  return (
    <>
      <CoverUpload
        previewUrl={coverPreviewUrl || ""}
        onPickFile={setCoverFromFile}
        variant="onboarding"
      />

      <p className="text-sm leading-5 text-contrast/30">
        Projects help organize and implement access control among your jobs
        between different departments. For example, you can group all of your
        marketing-related positions into a project called marketing.
      </p>

      <FormField
        control={control}
        name="project.title"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Project Title</FormLabel>
            <div className="relative">
              <FormControl>
                <Input
                  type="text"
                  placeholder="e.g Engineering"
                  maxLength={100}
                  className="pr-[122px]"
                  {...field}
                />
              </FormControl>
              <div className="absolute right-0 top-0 bottom-0 z-10 flex items-center pr-2">
                <WebImport
                  onImport={handleWebImport}
                  isLoading={webImportMutation.isPending}
                >
                  <WebImportButton />
                </WebImport>
              </div>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="project.access_level"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Access Level</FormLabel>
            <AccessLevelField value={field.value} onChange={field.onChange} />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="project.job_title"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Job Title</FormLabel>
            <FormControl>
              <Input
                type="text"
                placeholder="e.g Senior Software Engineer - Backend"
                maxLength={255}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
